"use client";

import { useEffect, useState, useRef } from "react";
import gsap from "gsap";
import { CustomEase } from "gsap/CustomEase";
import { useLenis } from "./LenisProvider"; // Adjust based on your Lenis setup
import Image from "next/image";
import { useLocale } from "next-intl";
import { useTranslations } from 'next-intl';
gsap.registerPlugin(CustomEase);
CustomEase.create("hop", "0.9, 0, 0.1, 1");

// Types
interface Project {
  name: string;
  director: string;
  location: string;
}

interface PreloaderProps {
  skipPreloader?: boolean;
}

const Preloader: React.FC<PreloaderProps> = ({ skipPreloader = false }) => {

 const te = useTranslations('tophero');
  
   const l = useLocale();
  function imagess() {
    return l == "ar" ? "/logo-black/شعار-بادليف-أسود.webp" :
      (l == "de" ? "/logo-black/padlev-logo-schwarz.webp" :
        (l == "es" ? "/logo-black/padlev-logo-negro.webp" :
          (l == "fr" ? "/logo-black/padlev-logo-noir.webp" :
            (l == "it" ? "/logo-black/padlev-logo-nero.webp" :
              (l == "nl" ? "/logo-black/padlev-logo-zwart.webp" :
                (l == "pt" ? "/logo-black/padlev-logo-preto.webp" :
                  (l == "ru" ? "/logo-black/padlev-logo-chernyy.webp" :
                    (l == "sv" ? "/logo-black/padlev-logo-svart.webp" :
                      "/logo-black/padlev-logo-black.webp"
                    )
                  )
                )
              )
            )
          )
        )
      );
  }



  const [hasSeenPreloader, setHasSeenPreloader] = useState<boolean>(false);
  const [isLargeScreen, setIsLargeScreen] = useState<boolean>(() => {
    if (typeof window === "undefined") {
      return true;
    }
    return window.innerWidth >= 1024;
  });
  const [images, setImages] = useState<string[]>([]);
  const overlayRef = useRef<HTMLDivElement>(null);
  const imageGridRef = useRef<HTMLDivElement>(null);
  
  // Get Lenis context - using type assertion to handle different possible structures
  const lenisContext = useLenis();
  
  // Try to extract Lenis instance from context
  // Common patterns: lenisContext could be the instance directly, or { lenis }, or { instance }
  const lenis = (lenisContext as any)?.lenis || 
                (lenisContext as any)?.instance || 
                lenisContext;

  // Project data
  const projectsData: Project[] = [
    {
      name: "Lunar Eclipse",
      director: "Amelia Crawford",
      location: "Toronto, ON",
    },
    {
      name: "Visitor Quarters",
      director: "Marcus Reynolds",
      location: "Vancouver Studio, BC",
    },
    {
      name: "Celestial",
      director: "Nina Liu // Weston",
      location: "Austin, TX",
    },
    {
      name: "Streamwave Original",
      director: "Dylan Pierce",
      location: "Sunset Studios - Miami",
    },
    {
      name: "Viewfinder",
      director: "Javier // Rodriguez",
      location: "BLANK Studios - Chicago",
    },
    {
      name: "Rhythm Collective",
      director: "Sophia // Chen",
      location: "London, UK",
    },
    {
      name: "Urban Odyssey",
      director: "Leo Thompson",
      location: "Pioneer Studios - Seattle",
    },
    {
      name: "Prism No. 1",
      director: "Taylor // McKnight",
      location: "Private Estate - Sedona",
    },
    {
      name: "Vision Quest",
      director: "Spencer // Hudson",
      location: "Elevation - Denver",
    },
    {
      name: "Wavelength",
      director: "Kai Nakamura",
      location: "San Francisco, CA",
    },
    {
      name: "Desert Horizon",
      director: "Olivia",
      location: "New Mexico",
    },
    {
      name: "Spectrum",
      director: "Ellis // Moss",
      location: "Harmony Studio - Montreal",
    },
    {
      name: "Vision Quest II",
      director: "Hudson // Wright",
      location: "Elevation Studios - Denver",
    },
    {
      name: "Auteur",
      director: "Leo Thompson",
      location: "Berlin, DE",
    },
    {
      name: "Capsule X Design",
      director: "Sophia // Chen",
      location: "Neon House - Brooklyn",
    },
    {
      name: "Pulse",
      director: "Callum // Winters",
      location: "Echo Pavilion - Portland",
    },
  ];

  // Generate image sources
  const allImageSources = Array.from(
    { length: 20 },
    (_, i) => `/spotlight/spotlight-${i + 1}.avif`
  );

  // Get random image set
  const getRandomImageSet = (): string[] => {
    const shuffled = [...allImageSources].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 9);
  };

  // Start image rotation
  const startImageRotation = () => {
    const gridImages = gsap.utils.toArray(".img");
    const totalCycles = 20;

    for (let cycle = 0; cycle < totalCycles; cycle++) {
      const randomImages = getRandomImageSet();

      gsap.to(
        {},
        {
          duration: 0,
          delay: cycle * 0.15,
          onComplete: () => {
            gridImages.forEach((imgElement, index) => {
              const img = (imgElement as HTMLElement).querySelector("img");
              if (img && randomImages[index]) {
                img.src = randomImages[index];
              }
            });
          },
        }
      );
    }
  };

  // Cleanup preloader
  const cleanupPreloader = () => {
    if (overlayRef.current) {
      gsap.to(overlayRef.current, {
        opacity: 0,
        duration: 0.5,
        onComplete: () => {
          if (overlayRef.current) {
            overlayRef.current.style.display = "none";
          }
          if (imageGridRef.current) {
            imageGridRef.current.style.display = "none";
          }
          
          // Kill all tweens
          gsap.killTweensOf([
            ".overlay",
            ".image-grid",
            ".projects",
            ".locations",
            ".loader",
            ".project-item",
            ".location-item",
            ".projects-header",
            ".locations-header",
            ".logo-line-1",
            ".logo-line-2",
            ".img",
          ]);

          // Enable scrolling - with multiple fallback approaches
          if (lenis) {
            // Try different possible method names
            if (typeof lenis.start === "function") {
              lenis.start();
            } else if (typeof lenis.resume === "function") {
              lenis.resume();
            } else if (typeof (lenis as any).lenis?.start === "function") {
              (lenis as any).lenis.start();
            }
          }
          
          // Always enable scrolling via CSS as fallback
          document.body.style.overflow = "auto";
          document.documentElement.style.overflow = "auto";

          // Mark as seen
          sessionStorage.setItem("preloaderSeen", "true");
          setHasSeenPreloader(true);
        },
      });
    }
  };

  // Create animation timelines
  const createAnimationTimelines = () => {
    const overlayTimeline = gsap.timeline();
    const imagesTimeline = gsap.timeline();

    // Logo text reveal
    overlayTimeline.to(".logo-line-1", {
      backgroundPosition: "0% 0%",
      color: "#e3e4d8",
      duration: 1,
      ease: "none",
      delay: 0.5,
      onComplete: () => {
        gsap.to(".logo-line-2", {
          backgroundPosition: "0% 0%",
          color: "#e3e4d8",
          duration: 1,
          ease: "none",
        });
      },
    });

    // Projects appear
    overlayTimeline.to([".projects-header", ".project-item"], {
      opacity: 1,
      duration: 0.05,
      stagger: 0.075,
      delay: 1,
    });

    // Locations appear
    overlayTimeline.to(
      [".locations-header", ".location-item"],
      {
        opacity: 1,
        duration: 0.05,
        stagger: 0.075,
      },
      "<"
    );

    // Text color change
    overlayTimeline.to(".project-item", {
      color: "#e3e4d8",
      duration: 0.15,
      stagger: 0.075,
    });

    overlayTimeline.to(
      ".location-item",
      {
        color: "#e3e4d8",
        duration: 0.15,
        stagger: 0.075,
      },
      "<"
    );

    // Fade out projects
    overlayTimeline.to([".projects-header", ".project-item"], {
      opacity: 0,
      duration: 0.05,
      stagger: 0.075,
    });

    // Fade out locations
    overlayTimeline.to(
      [".locations-header", ".location-item"],
      {
        opacity: 0,
        duration: 0.05,
        stagger: 0.075,
      },
      "<"
    );

    // Fade out overlay
    overlayTimeline.to(".overlay", {
      opacity: 0,
      duration: 0.5,
      delay: 1.5,
    });

    // Reveal images
    imagesTimeline.to(".img", {
      clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
      duration: 1,
      delay: 2.5,
      stagger: 0.05,
      ease: "hop",
      onStart: () => {
        setTimeout(() => {
          startImageRotation();
          gsap.to(".loader", { opacity: 0, duration: 0.3 });
        }, 1000);
      },
    });

    // Hide images and complete
    imagesTimeline.to(".img", {
      clipPath: "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)",
      duration: 1,
      delay: 2.5,
      stagger: 0.05,
      ease: "hop",
      onComplete: () => {
        setTimeout(() => {
          cleanupPreloader();
        }, 500);
      },
    });
  };

 


  // Initialize
  const initializePreloader = () => {
    setImages(getRandomImageSet());
    
    // Disable scrolling during preloader - with multiple fallback approaches
    if (lenis) {
      // Try different possible method names
      if (typeof lenis.stop === "function") {
        lenis.stop();
      } else if (typeof lenis.pause === "function") {
        lenis.pause();
      } else if (typeof (lenis as any).lenis?.stop === "function") {
        (lenis as any).lenis.stop();
      }
    }
    
    // Always disable scrolling via CSS as fallback
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    // Start animations after a brief delay
    setTimeout(() => {
      createAnimationTimelines();
    }, 100);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    const updateScreenSize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
    };

    updateScreenSize();
    window.addEventListener("resize", updateScreenSize);

    return () => {
      window.removeEventListener("resize", updateScreenSize);
    };
  }, []);

  useEffect(() => {
    if (!isLargeScreen) {
      setHasSeenPreloader(true);
      if (overlayRef.current) overlayRef.current.style.display = "none";
      if (imageGridRef.current) imageGridRef.current.style.display = "none";
      document.body.style.overflow = "auto";
      document.documentElement.style.overflow = "auto";
      return;
    }

    // Check if preloader has been seen
    const seen = sessionStorage.getItem("preloaderSeen") === "true";
    setHasSeenPreloader(seen || skipPreloader);

    if (seen || skipPreloader) {
      // Skip preloader
      if (overlayRef.current) overlayRef.current.style.display = "none";
      if (imageGridRef.current) imageGridRef.current.style.display = "none";
      
      // Ensure scrolling is enabled
      document.body.style.overflow = "auto";
      document.documentElement.style.overflow = "auto";
      
      // Try to start Lenis if available
      if (lenis) {
        if (typeof lenis.start === "function") {
          lenis.start();
        } else if (typeof lenis.resume === "function") {
          lenis.resume();
        }
      }
    } else {
      // Show preloader
      initializePreloader();
    }

    return () => {
      // Cleanup GSAP animations on unmount
      gsap.killTweensOf("*");
      
      // Ensure scrolling is re-enabled if component unmounts unexpectedly
      document.body.style.overflow = "auto";
      document.documentElement.style.overflow = "auto";
    };
  }, [isLargeScreen, lenis, skipPreloader]);

  // Don't render if preloader has been seen or should be skipped
  if (hasSeenPreloader || !isLargeScreen) {
    return null;
  }

  return (
    <>
      {/* Overlay */}
      <div
        ref={overlayRef}
        className="fixed top-0 left-0 z-50 w-screen h-svh flex flex-col lg:flex-row gap-8 p-8 bg-yel text-gray-700 pointer-events-auto overflow-hidden"
      >
        {/* Projects Column */}
        <div className="flex-1 hidden lg:flex flex-col justify-center">
          <div className="projects-header flex gap-8 opacity-0">
            <span className="flex-1">COURTS</span>
            <span className="flex-1">PLAYERS</span>
          </div>
          <div className="projects flex-1 flex flex-col justify-center">
            {projectsData.map((project, index) => (
              <div
                key={index}
                className="project-item flex gap-8 opacity-0 text-gray-500"
              >
                <p className="flex-1 text-base">{project.name}</p>
                <p className="flex-1 text-base">{project.director}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Loader Column */}
        <div className="flex-1 flex flex-col justify-center items-center">
          <div className="loader flex flex-col items-center">

            <div className="relative h-5 w-32 ">
                         <Image
                           src={imagess()} // or "/logo.webp" if using an webp
                           alt={te('logo-padlev')}
                           fill
                           sizes='100%'
                           style={{ objectFit: 'contain' }} // Maintain aspect ratio
                           priority // Ensures it loads faster
                         />
                       </div>

          </div>
        </div>

        {/* Locations Column */}
        <div className="flex-1 hidden lg:flex flex-col justify-center items-center">
          <div className="locations-header flex gap-8 opacity-0 w-1/2">
            <span>LOCATIONS</span>
          </div>
          <div className="locations flex-1 flex flex-col justify-center">
            {projectsData.map((project, index) => (
              <div
                key={index}
                className="location-item flex gap-8 opacity-0 text-gray-500 w-1/2"
              >
                <p className="text-base">{project.location}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Image Grid */}
      <div
        ref={imageGridRef}
        className="fixed top-1/2 left-1/2 z-50 w-[90%] md:w-[60%] lg:w-[30%] aspect-square -translate-x-1/2 -translate-y-1/2 flex flex-col gap-4"
      >
        {/* Grid Rows */}
        {[0, 1, 2].map((row) => (
          <div key={row} className="grid-row flex gap-4">
            {[0, 1, 2].map((col) => {
              const index = row * 3 + col;
              return (
                <div
                  key={col}
                  className="img flex-1 aspect-square overflow-hidden clip-path-square"
                  style={{
                    clipPath: "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)",
                  }}
                >
                  <img
                    src={images[index] || "/placeholder.jpg"}
                    alt=""
                    className="w-full h-full object-cover aspect-square"
                  />
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
};

export default Preloader;